import { useQuery } from "@tanstack/react-query";
import { customersApi } from "@/app/lib/api/customers";
import { Customer } from "@/app/lib/supabase/types";
import { useAppStore } from "@/app/lib/stores/useAppStore";

const CUSTOMER_STALE_TIME = 10 * 60 * 1000; // 10 minutes
const CUSTOMER_GC_TIME = 7 * 24 * 60 * 60 * 1000; // 7 days

export const customerQueryKeys = {
  all: ["customers"] as const,
  byCompany: (companyId: string) =>
    [...customerQueryKeys.all, "company", companyId] as const,
  detail: (customerId: string) =>
    [...customerQueryKeys.all, "detail", customerId] as const,
};

/**
 * Hook to get all customers for the selected company
 * @returns Customers
 */
export function useCustomers() {
  const { selectedCompanyId } = useAppStore();

  return useQuery<Customer[]>({
    queryKey: customerQueryKeys.byCompany(selectedCompanyId || ""),
    queryFn: () => {
      if (!selectedCompanyId) throw new Error("No company selected");
      return customersApi.getCustomers(selectedCompanyId);
    },
    enabled: !!selectedCompanyId,
    staleTime: CUSTOMER_STALE_TIME,
    gcTime: CUSTOMER_GC_TIME,
  });
}

/**
 * Hook to get all customers for a specific company
 * @param companyId - The ID of the company to get customers for
 * @returns Customers
 */
export function useCustomersByCompany(companyId: string | undefined) {
  return useQuery<Customer[]>({
    queryKey: customerQueryKeys.byCompany(companyId || ""),
    queryFn: () => {
      if (!companyId) throw new Error("No company ID provided");
      return customersApi.getCustomers(companyId);
    },
    enabled: !!companyId,
    staleTime: CUSTOMER_STALE_TIME,
    gcTime: CUSTOMER_GC_TIME,
  });
}

/**
 * Hook to get a customer by ID
 * @param customerId - The ID of the customer to get
 * @returns Customer
 */
export function useCustomer(customerId: string | undefined) {
  return useQuery<Customer>({
    queryKey: customerQueryKeys.detail(customerId || ""),
    queryFn: () => {
      if (!customerId) throw new Error("No customer ID provided");
      return customersApi.getCustomer(customerId);
    },
    enabled: !!customerId,
    staleTime: CUSTOMER_STALE_TIME,
    gcTime: CUSTOMER_GC_TIME,
  });
}